/**
 * Maps UI column labels to CSV export column names
 * This mapping ensures that visible columns in the UI are correctly exported
 * to the CSV file with the proper column headers.
 * CSV column names must match exactly with backend ExportTicketsCsvMap column names.
 * Includes both current labels (mockedColumns) and legacy labels (COLUMN_LABEL_MIGRATION in ColumnPickerSettings).
 */
export const UI_TO_CSV_COLUMN_MAP: Record<string, string> = {
  // P / Priority
  P: 'Priority',
  Priority: 'Priority',
  // RO
  RO: 'RO Number',
  'RO number': 'RO Number',
  'RO Number': 'RO Number',
  // Line
  Line: 'Line Number',
  'Line number': 'Line Number',
  // Status
  Status: 'Status',
  // Age
  Age: 'Age',
  'Age (days)': 'Age',
  // Claim Age
  'Claim Age': 'Claim Age',
  'Claim Age (days)': 'Claim Age',
  // Assignee
  Assignee: 'Assignee',
  'Assigned to:': 'Assignee',
  // Category
  Category: 'Category',
  // Last Comment
  'Last Comment': 'Last Comment',
  // Claim Status
  'Claim Status': 'Claim Status',
  // Amount
  Amount: 'Amount',
  // Advisor
  Advisor: 'Advisor',
  'Advisor number': 'Advisor',
  // Tech
  Tech: 'Tech',
  'Tech number': 'Tech',
  // Attachment
  Attachment: 'Attachment',
  // Approved
  Approved: 'Approved',
  // Subcategory
  Subcategory: 'Sub Category',
  // Closed RO Age
  'Closed RO Age': 'Closed RO Age',
  // Account
  Account: 'Account#',
  'Account Number': 'Account#',
  'Work Type': 'Work Type',
  // Audit Trail
  'Audit Trail': 'Audit Trail',
};

/** Allowed CSV column names for Schedule tab export only. No other columns may be exported for Schedule. */
export const SCHEDULE_EXPORT_CSV_WHITELIST = [
  'Dealer',
  'RO Number',
  'Amount',
  'Closed RO Age',
  'Sub Category',
  'Account#',
  'Last Comment',
] as const;

const SCHEDULE_WHITELIST_SET = new Set<string>(SCHEDULE_EXPORT_CSV_WHITELIST);

/**
 * Columns that should always be included in schedule exports (not in the UI column picker).
 * Must match the first entry of SCHEDULE_EXPORT_CSV_WHITELIST.
 */
export const REQUIRED_EXPORT_COLUMNS = ['Dealer'] as const;

/**
 * Returns CSV export column names for Schedule tab only.
 * Uses UI_TO_CSV_COLUMN_MAP; only columns in SCHEDULE_EXPORT_CSV_WHITELIST are exported; order follows visibleColumnLabels.
 * REQUIRED_EXPORT_COLUMNS are always first.
 * @param visibleColumnLabels - Visible column labels in user order (from visibleColumns_schedule)
 * @returns CSV column names in order: REQUIRED_EXPORT_COLUMNS, then visible allowed columns in their order
 */
export const getScheduleExportColumnsToExport = (visibleColumnLabels: string[]): string[] => {
  const mapped = visibleColumnLabels
    .map((label): string | undefined => UI_TO_CSV_COLUMN_MAP[label])
    .filter((csv): csv is string => csv != null && SCHEDULE_WHITELIST_SET.has(csv));
  return [...REQUIRED_EXPORT_COLUMNS, ...mapped];
};
